import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  FileText,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Play,
  Pause,
  X,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { useGenerationUpdates } from '../services/websocket'
import { Generation, GenerationFilters } from '../types'

export default function GenerationsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<GenerationFilters>({})
  const [showFilters, setShowFilters] = useState(false)

  // Real-time updates
  useGenerationUpdates((data) => {
    console.log('Generation Update:', data)
  })

  const { data: generationsData, isLoading } = useQuery(
    ['generations', page, filters],
    () => adminApi.getGenerations(page, 20, filters),
    {
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  )

  const generations = generationsData?.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Generations
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor AI generation queue and processing status
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by user ID, style, or room type..."
                  className="input pl-10"
                  value={filters.user_id || ''}
                  onChange={(e) => setFilters({ ...filters, user_id: e.target.value || undefined })}
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <select
                className="input"
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>

              <select
                className="input"
                value={filters.quality || ''}
                onChange={(e) => setFilters({ ...filters, quality: e.target.value || undefined })}
              >
                <option value="">All Quality</option>
                <option value="standard">Standard</option>
                <option value="high">High</option>
                <option value="ultra">Ultra</option>
              </select>

              <input
                type="date"
                className="input"
                placeholder="Created After"
                value={filters.created_after || ''}
                onChange={(e) => setFilters({ ...filters, created_after: e.target.value || undefined })}
              />

              <select
                className="input"
                value={filters.has_errors === undefined ? '' : filters.has_errors.toString()}
                onChange={(e) => setFilters({ 
                  ...filters, 
                  has_errors: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
              >
                <option value="">All</option>
                <option value="true">With Errors</option>
                <option value="false">No Errors</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Generations Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Style</th>
                  <th>Room Type</th>
                  <th>Quality</th>
                  <th>Status</th>
                  <th>Credits</th>
                  <th>Processing Time</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {generations.map((generation) => (
                  <GenerationRow key={generation.id} generation={generation} />
                ))}
              </tbody>
            </table>
          </div>

          {generations.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No generations found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No generations match your current filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function GenerationRow({ generation }: { generation: Generation }) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500 animate-spin" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const badgeClass = {
      pending: 'badge-gray',
      processing: 'badge-warning',
      completed: 'badge-success',
      failed: 'badge-danger'
    }[status] || 'badge-gray'

    return <span className={`badge ${badgeClass}`}>{status}</span>
  }

  return (
    <tr>
      <td className="font-mono text-sm">{generation.id.slice(0, 8)}...</td>
      <td className="font-mono text-sm">{generation.user_id.slice(0, 8)}...</td>
      <td>{generation.style || 'N/A'}</td>
      <td>{generation.room_type || 'N/A'}</td>
      <td>
        <span className="badge badge-gray capitalize">{generation.quality}</span>
      </td>
      <td>
        <div className="flex items-center space-x-2">
          {getStatusIcon(generation.status)}
          {getStatusBadge(generation.status)}
        </div>
      </td>
      <td>{generation.credits_used}</td>
      <td>
        {generation.processing_time_seconds 
          ? `${generation.processing_time_seconds}s`
          : generation.status === 'processing' 
          ? <span className="text-yellow-600">Processing...</span>
          : 'N/A'
        }
      </td>
      <td>
        {generation.created_at && generation.created_at !== '' 
          ? new Date(generation.created_at).toLocaleDateString() 
          : 'N/A'
        }
      </td>
      <td>
        <div className="flex items-center space-x-2">
          <button 
            className="text-primary-600 hover:text-primary-900"
            onClick={async () => {
              try {
                const result = await adminApi.getGeneration(generation.id)
                if (result.success) {
                  // TODO: Open generation details modal
                  alert(`Generation Details:\nID: ${result.data.id}\nStatus: ${result.data.status}\nStyle: ${result.data.style}\nRoom: ${result.data.room_type}`)
                }
              } catch (error) {
                alert('Failed to load generation details')
              }
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
          {generation.status === 'failed' && (
            <button 
              className="text-green-600 hover:text-green-900"
              onClick={async () => {
                try {
                  const result = await adminApi.retryGeneration(generation.id)
                  if (result.success) {
                    alert('Generation queued for retry')
                    // TODO: Refresh generations list
                    window.location.reload()
                  }
                } catch (error) {
                  alert('Failed to retry generation')
                }
              }}
              title="Retry Generation"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}